import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../envelope/Sidebar';
import TopBar from '../envelope/TopBar';
import * as XLSX from 'xlsx';
import '../CreatePages/CreateGroupPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faFileExcel, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from './ConfirmModal';

const EditGroupPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [members, setMembers] = useState([{ id: Date.now(), userId: null, name: '', email: '' }]);
    const [importError, setImportError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const fileInputRef = useRef(null);

    // Загрузка данных группы
    useEffect(() => {
        const fetchGroupData = async () => {
            try {
                setIsLoading(true);
                const token = localStorage.getItem('authToken');
                const response = await fetch(`http://185.250.149.189:8000/api/v1/GroupUsers?id=${id}`, {
                    headers: { 
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (!response.ok) throw new Error('Ошибка загрузки данных группы');
                
                const data = await response.json();
                const group = data.groups_users.find(g => g.id === parseInt(id));
                
                if (!group) throw new Error('Группа не найдена');
                
                setGroupName(group.name);
                setGroupDescription(group.description || '');
                
                // Преобразуем пользователей в нужный формат
                const formattedMembers = group.users.map(user => ({
                    id: Date.now() + user.user_id, // уникальный ID для React
                    userId: user.user_id, // сохраняем оригинальный ID пользователя
                    name: user.user_name,
                    email: user.user_mail
                }));
                
                setMembers(formattedMembers);
                
            } catch (error) {
                console.error('Ошибка загрузки данных:', error);
                setSaveStatus({ type: 'error', message: error.message });
            } finally {
                setIsLoading(false);
            }
        };

        fetchGroupData();
    }, [id]);

    // Обработчики изменений
    const handleNameChange = (e) => setGroupName(e.target.value);
    const handleDescriptionChange = (e) => setGroupDescription(e.target.value);
    
    const handleMemberNameChange = (id, value) => {
        setMembers(members.map(member => 
            member.id === id ? {...member, name: value} : member
        ));
    };

    const handleMemberEmailChange = (id, value) => {
        setMembers(members.map(member => 
            member.id === id ? {...member, email: value} : member
        ));
    };

    const handleAddMember = () => {
        setMembers([...members, { id: Date.now(), userId: null, name: '', email: '' }]);
    };

    const handleRemoveMember = (id) => {
        if (members.length > 1) {
            setMembers(members.filter(member => member.id !== id));
        }
    };

    // Подготовка данных для сохранения
    const prepareSaveData = () => {
        return {
            id: parseInt(id),
            name: groupName,
            users: members.map(member => ({
                user_id: member.userId || 0, // для новых пользователей
                user_name: member.name,
                user_mail: member.email
            }))
        };
    };

    // Отправка данных на сервер
    const handleSave = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('authToken');
            const saveData = prepareSaveData();
            
            const response = await fetch('http://185.250.149.189:8000/api/v1/GroupUsersUpdate', {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(saveData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка сохранения');
            }

            setSaveStatus({ type: 'success', message: 'Группа успешно обновлена!' });
            setTimeout(() => navigate('/groups'), 2000);
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            setSaveStatus({ type: 'error', message: `Ошибка: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    // Импорт из Excel
    const handleImportClick = () => fileInputRef.current.click();

    const handleFileUpload = (e) => {
        setImportError('');
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                
                if (jsonData.length === 0) {
                    setImportError('Файл не содержит данных');
                    return;
                }
                
                const importedMembers = jsonData.map((row, index) => {
                    const nameKey = Object.keys(row).find(key => 
                        key.toLowerCase().includes('имя') || 
                        key.toLowerCase().includes('name') ||
                        key.toLowerCase().includes('фио')
                    );
                    
                    const emailKey = Object.keys(row).find(key => 
                        key.toLowerCase().includes('email') || 
                        key.toLowerCase().includes('почта') ||
                        key.toLowerCase().includes('e-mail')
                    );
                    
                    return {
                        id: Date.now() + index,
                        userId: null,
                        name: nameKey ? String(row[nameKey]) : '',
                        email: emailKey ? String(row[emailKey]) : ''
                    };
                }).filter(member => member.name || member.email);
                
                if (importedMembers.length === 0) {
                    setImportError('Не найдены подходящие данные для импорта');
                    return;
                }
                
                setMembers(importedMembers);
            } catch (error) {
                console.error('Ошибка импорта:', error);
                setImportError('Ошибка при чтении файла. Убедитесь, что файл в правильном формате.');
            }
        };
        
        reader.readAsArrayBuffer(file);
        e.target.value = null;
    };

    // Подтверждение сохранения
    const confirmSave = () => {
        setShowConfirmModal(true);
    };

    return (
        <div className="dashboard">
            <Sidebar 
                isCollapsed={isSidebarCollapsed} 
                toggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            />
            <TopBar isCollapsed={isSidebarCollapsed} />
            <main className={`main-content ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="content-container">
                    <div className="GroupPattern"> 
                        <div className="GroupPatternHead"> 
                            <h1>Редактирование группы</h1>
                            <button 
                                className="SaveButton" 
                                onClick={confirmSave}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Сохранение...</>
                                ) : 'Сохранить'}
                            </button>
                        </div>
                        
                        {saveStatus && (
                            <div className={`save-status ${saveStatus.type}`}>
                                {saveStatus.message}
                            </div>
                        )}
                        
                        <div className="GroupPatternDescription"> 
                            <div className="GroupName">
                                Название группы
                                <textarea 
                                    className='input1' 
                                    value={groupName}
                                    onChange={handleNameChange}
                                    placeholder="Введите название группы"
                                    disabled={isLoading}
                                ></textarea>
                            </div>
                            <div className="GroupDes">
                                Описание <br/> группы
                                <textarea 
                                    className='input2' 
                                    value={groupDescription}
                                    onChange={handleDescriptionChange}
                                    placeholder="Опишите группу"
                                    disabled={isLoading}
                                ></textarea>
                            </div>
                        </div>  

                        <div className="import-section">
                            <button 
                                className="import-excel-btn" 
                                onClick={handleImportClick}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faFileExcel} /> Импортировать из Excel
                            </button>
                            <input 
                                type="file" 
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".xlsx, .xls, .csv" 
                                style={{ display: 'none' }}
                                disabled={isLoading}
                            />
                            {importError && <div className="import-error">{importError}</div>}
                        </div>

                        <div className='GroupBoard'>
                            {members.map((member) => (
                                <div className='BoardItem' key={member.id}>
                                    <div className='ItemName'>
                                        <label>Фамилия, имя <span className='detail-label'>(сначала фамилия)</span></label>
                                        <input 
                                            type='text' 
                                            value={member.name}
                                            onChange={(e) => handleMemberNameChange(member.id, e.target.value)}
                                            placeholder="Введите фамилию и имя"
                                            disabled={isLoading}
                                        /> 
                                    </div>
                                    <div className='ItemEmail'>
                                        <label>Email</label>
                                        <input 
                                            type='email' 
                                            value={member.email}
                                            onChange={(e) => handleMemberEmailChange(member.id, e.target.value)}
                                            placeholder="Введите email"
                                            disabled={isLoading}
                                        /> 
                                    </div>
                                    
                                    <div className="member-actions">
                                        {members.length > 1 && (
                                            <button 
                                                className="remove-member-btn"
                                                onClick={() => handleRemoveMember(member.id)}
                                                title="Удалить участника"
                                                disabled={isLoading}
                                            >
                                                <FontAwesomeIcon icon={faTrashAlt} />
                                            </button>
                                        )}
                                        
                                        {member.id === members[members.length - 1].id && (
                                            <button 
                                                className="add-member-btn"
                                                onClick={handleAddMember}
                                                title="Добавить участника"
                                                disabled={isLoading}
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>   
                    </div>
                </div>
            </main>
            
            {/* Модальное окно подтверждения */}
                {showConfirmModal && (
                <ConfirmModal 
                    title="Подтверждение сохранения"
                    message="Вы уверены, что хотите сохранить изменения в группе?"
                    onConfirm={() => {
                    handleSave();
                    setShowConfirmModal(false);
                    }}
                    onCancel={() => setShowConfirmModal(false)}
                />
            )}
        </div>
    );
};

export default EditGroupPage;